/**
 * SubappaltiTab — Tab di gestione subappalti del cantiere.
 *
 * Lista + form completo (anagrafica ditta, contratto, documenti,
 * pagamenti SAL, collegamento attività Gantt).
 * Admin-only.
 */
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Plus, X, Pencil, Trash2, Save, HardHat, Receipt, Paperclip,
  Calendar as CalIcon, AlertTriangle, CheckCircle2, Ban,
  FileSpreadsheet, FileDown,
} from "lucide-react";
import api from "@/api";
import AiExtractBox from "@/components/AiExtractBox";

const STATO_META = {
  bozza:       { label: "Bozza",       color: "bg-slate-200 text-slate-700" },
  attivo:      { label: "Attivo",      color: "bg-blue-100 text-blue-800" },
  completato:  { label: "Completato",  color: "bg-emerald-100 text-emerald-800" },
  annullato:   { label: "Annullato",   color: "bg-rose-100 text-rose-800" },
};

const TIPI_PAGAMENTO = [
  { k: "acconto", l: "Acconto" },
  { k: "sal",     l: "SAL" },
  { k: "saldo",   l: "Saldo" },
  { k: "altro",   l: "Altro" },
];

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n || 0));
const isoDate = (d = new Date()) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);

const EMPTY_FORM = {
  ragione_sociale: "",
  partita_iva: "",
  codice_fiscale: "",
  referente: "",
  telefono: "",
  email: "",
  indirizzo: "",
  descrizione_lavori: "",
  importo_imponibile: 0,
  aliquota_iva: 22,
  data_inizio: "",
  data_fine_prevista: "",
  data_fine_effettiva: "",
  durc_scadenza: "",
  contratto_base64: null,
  allegati: [],
  note: "",
  stato: "bozza",
  percentuale_avanzamento: 0,
  include_in_economia: true,
  attivita_collegate: [],
};

export default function SubappaltiTab({ cantiereId, attivitaOptions = [] }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [detailId, setDetailId] = useState(null);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["subappalti", cantiereId],
    queryFn: () => api.get("/subappalti", { params: { cantiere_id: cantiereId } }).then((r) => r.data),
    enabled: !!cantiereId,
  });

  // Sempre derivato dai dati live della query — così l'aggiunta/rimozione
  // di un pagamento aggiorna immediatamente il modale dopo invalidateQueries.
  const detailFor = detailId ? items.find((s) => s.subappalto_id === detailId) : null;

  const { data: summary } = useQuery({
    queryKey: ["subappalti-summary", cantiereId],
    queryFn: () => api.get(`/subappalti/cantiere/${cantiereId}/summary`).then((r) => r.data),
    enabled: !!cantiereId,
  });

  const save = useMutation({
    mutationFn: async (data) => {
      const { invia_contratto, ...campi } = data;
      const payload = { ...campi, cantiere_id: cantiereId };
      const saved = editing
        ? (await api.patch(`/subappalti/${editing.subappalto_id}`, payload)).data
        : (await api.post("/subappalti", payload)).data;
      if (invia_contratto) {
        try {
          await api.post(`/subappalti/${saved.subappalto_id}/invia-contratto`, {
          destinatario: data.email.trim(),
          oggetto: `Contratto di subappalto - ${data.ragione_sociale}`,
          messaggio: "In allegato il contratto di subappalto. Cordiali saluti, Ethos Costruzioni",
          allegati_indici: [],
          conferma: true,
        });
        } catch (error) {
          return {
            ...saved,
            invioErrore: error.response?.data?.detail || "Esito email non verificato",
          };
        }
      }
      return { ...saved, invioRichiesto: !!invia_contratto };
    },
    onSuccess: (result) => {
      if (result.invioErrore) toast.error("Salvato, ma invio email non confermato: " + result.invioErrore);
      else if (result.invioRichiesto) toast.success("Salvato. Email accettata dal server SMTP.");
      else toast.success(editing ? "Subappalto aggiornato" : "Subappalto creato");
      qc.invalidateQueries({ queryKey: ["subappalti", cantiereId] });
      qc.invalidateQueries({ queryKey: ["subappalti-summary", cantiereId] });
      qc.invalidateQueries({ queryKey: ["cantiere-econ", cantiereId] });
      closeForm();
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/subappalti/${id}`).then((r) => r.data),
    onSuccess: () => {
      toast.success("Subappalto eliminato");
      qc.invalidateQueries({ queryKey: ["subappalti", cantiereId] });
      qc.invalidateQueries({ queryKey: ["subappalti-summary", cantiereId] });
      qc.invalidateQueries({ queryKey: ["cantiere-econ", cantiereId] });
    },
  });

  const openNew = () => { setEditing(null); setForm(EMPTY_FORM); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      ...EMPTY_FORM,
      ...s,
      data_inizio: s.data_inizio || "",
      data_fine_prevista: s.data_fine_prevista || "",
      data_fine_effettiva: s.data_fine_effettiva || "",
      durc_scadenza: s.durc_scadenza || "",
    });
    setOpen(true);
  };
  const closeForm = () => { setOpen(false); setEditing(null); setForm(EMPTY_FORM); };

  const totIVA = (Number(form.importo_imponibile) || 0) * (Number(form.aliquota_iva) || 0) / 100;
  const totCon = (Number(form.importo_imponibile) || 0) + totIVA;

  const handleFileToBase64 = (file, key) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error("File troppo grande (max 4MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, [key]: reader.result }));
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-4" data-testid="subappalti-tab">
      {/* KPI riepilogo */}
      {summary && summary.conteggio > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI label="Subappalti attivi" value={summary.conteggio} />
          <KPI label="Imponibile totale" value={eur(summary.totale_imponibile)} />
          <KPI label="Costo con IVA" value={eur(summary.totale_con_iva)} highlight="rose" />
          <KPI label="Residuo da pagare" value={eur(summary.residuo)} highlight={summary.residuo > 0 ? "amber" : "emerald"} />
        </div>
      )}

      {/* Header con + Nuovo */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="overline">Cantiere</div>
          <h2 className="font-display text-2xl font-bold flex items-center gap-2">
            <HardHat className="w-5 h-5 text-slate-500" /> Subappalti
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            L'importo dei subappalti attivi (esclusi Bozza/Annullato) entra automaticamente nei costi del cantiere.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {items.length > 0 && (
            <>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/export/subappalti/excel?cantiere_id=${cantiereId}`}
                target="_blank"
                rel="noreferrer"
                data-testid="btn-export-excel"
                className="inline-flex items-center gap-2 px-3 py-2 border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 text-xs font-semibold rounded-sm"
                title="Scarica Excel di questo cantiere"
              >
                <FileSpreadsheet className="w-4 h-4" /> Excel
              </a>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/export/subappalti/pdf?cantiere_id=${cantiereId}`}
                target="_blank"
                rel="noreferrer"
                data-testid="btn-export-pdf"
                className="inline-flex items-center gap-2 px-3 py-2 border border-rose-300 bg-rose-50 hover:bg-rose-100 text-rose-800 text-xs font-semibold rounded-sm"
                title="Rendicontazione PDF al committente principale"
              >
                <FileDown className="w-4 h-4" /> PDF rendicontazione
              </a>
            </>
          )}
          <button
            onClick={openNew}
            data-testid="btn-nuovo-subappalto"
            className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold rounded-sm"
          >
            <Plus className="w-4 h-4" /> Nuovo subappalto
          </button>
        </div>
      </div>

      {/* Lista */}
      {isLoading ? (
        <div className="p-8 text-center text-sm text-slate-400">Caricamento…</div>
      ) : items.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-400 bg-white border border-slate-200 rounded-sm">
          Nessun subappalto per questo cantiere.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-600 uppercase tracking-widest">
              <tr>
                <th className="text-left p-3">Ditta / P.IVA</th>
                <th className="text-left p-3">Lavori</th>
                <th className="text-right p-3">Importo IVA incl.</th>
                <th className="text-center p-3">Avanz.</th>
                <th className="text-center p-3">Stato</th>
                <th className="text-center p-3">DURC</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => {
                const durcScaduto = s.durc_scadenza && s.durc_scadenza < isoDate();
                return (
                  <tr key={s.subappalto_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`sub-row-${s.subappalto_id}`}>
                    <td className="p-3">
                      <div className="font-semibold text-slate-800">{s.ragione_sociale}</div>
                      <div className="text-[11px] text-slate-500 font-mono">{s.partita_iva || "—"}</div>
                    </td>
                    <td className="p-3 text-slate-600 max-w-md">
                      <div className="truncate" title={s.descrizione_lavori}>{s.descrizione_lavori}</div>
                    </td>
                    <td className="p-3 text-right font-mono font-bold">{eur(s.importo_totale)}</td>
                    <td className="p-3">
                      <div className="w-24 mx-auto">
                        <div className="text-[10px] text-center text-slate-500">{Math.round(s.percentuale_avanzamento || 0)}%</div>
                        <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-500" style={{ width: `${Math.max(0, Math.min(100, s.percentuale_avanzamento || 0))}%` }} />
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider ${STATO_META[s.stato]?.color || ""}`}>
                        {STATO_META[s.stato]?.label || s.stato}
                      </span>
                    </td>
                    <td className="p-3 text-center text-xs">
                      {s.durc_scadenza ? (
                        <span className={`inline-flex items-center gap-1 ${durcScaduto ? "text-rose-600 font-bold" : "text-slate-600"}`} title={`Scadenza DURC: ${s.durc_scadenza}`}>
                          {durcScaduto ? <AlertTriangle className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}
                          {s.durc_scadenza}
                        </span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <div className="inline-flex gap-1">
                        <button
                          onClick={() => setDetailId(s.subappalto_id)}
                          data-testid={`btn-detail-${s.subappalto_id}`}
                          title="Pagamenti / Attività"
                          className="p-1.5 text-emerald-700 hover:bg-emerald-50 rounded"
                        >
                          <Receipt className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openEdit(s)}
                          data-testid={`btn-edit-${s.subappalto_id}`}
                          title="Modifica"
                          className="p-1.5 text-slate-700 hover:bg-slate-100 rounded"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => window.confirm(`Eliminare il subappalto "${s.ragione_sociale}"?`) && del.mutate(s.subappalto_id)}
                          data-testid={`btn-del-${s.subappalto_id}`}
                          title="Elimina"
                          className="p-1.5 text-rose-600 hover:bg-rose-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Form modal */}
      {open && (
        <SubappaltoForm
          editing={editing}
          form={form}
          setForm={setForm}
          totIVA={totIVA}
          totCon={totCon}
          attivitaOptions={attivitaOptions}
          onClose={closeForm}
          onSave={(invia) => save.mutate({ ...form, invia_contratto: invia })}
          saving={save.isPending}
          handleFileToBase64={handleFileToBase64}
        />
      )}

      {/* Detail (pagamenti / attività) */}
      {detailFor && (
        <SubappaltoDetail
          subappalto={detailFor}
          attivitaOptions={attivitaOptions}
          onClose={() => setDetailId(null)}
          onChanged={() => {
            qc.invalidateQueries({ queryKey: ["subappalti", cantiereId] });
            qc.invalidateQueries({ queryKey: ["subappalti-summary", cantiereId] });
          }}
        />
      )}
    </div>
  );
}

function KPI({ label, value, highlight }) {
  const bg = {
    rose: "bg-rose-50 border-rose-200 text-rose-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
  }[highlight] || "bg-white border-slate-200 text-slate-900";
  return (
    <div className={`p-3 border rounded-sm ${bg}`}>
      <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">{label}</div>
      <div className="font-display font-black text-xl mt-1 truncate">{value}</div>
    </div>
  );
}

// ------------------- FORM MODALE -------------------

function SubappaltoForm({ editing, form, setForm, totIVA, totCon, attivitaOptions, onClose, onSave, saving, handleFileToBase64 }) {
  const [ricercaFornitore, setRicercaFornitore] = useState("");

  const { data: fornitori = [], isLoading: caricamentoFornitori } = useQuery({
    queryKey: ["fornitori-stats"],
    queryFn: () =>
      api.get("/entita/fornitori/stats").then((r) => r.data),
  });

  const fornitoriFiltrati = (Array.isArray(fornitori) ? fornitori : [])
    .filter((f) => {
      const ricerca = ricercaFornitore.toLowerCase().trim();
      return (
        (f.ragione_sociale || "").toLowerCase().includes(ricerca) ||
        (f.partita_iva || "").toLowerCase().includes(ricerca)
      );
    })
    .slice(0, 20);

  const selezionaFornitore = (f) => {
    setForm((precedente) => ({
      ...precedente,
      ragione_sociale: f.ragione_sociale || "",
      partita_iva: f.partita_iva || "",
      codice_fiscale: f.codice_fiscale || "",
      referente: f.referente || "",
      telefono: f.telefono || "",
      email: f.email || "",
      indirizzo: f.indirizzo || "",
    }));

    setRicercaFornitore("");
    toast.success("Dati del fornitore compilati automaticamente");
  };

  const [inviaContratto, setInviaContratto] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleAtt = (id) => {
    setForm((f) => {
      const list = new Set(f.attivita_collegate || []);
      list.has(id) ? list.delete(id) : list.add(id);
      return { ...f, attivita_collegate: [...list] };
    });
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-4xl border border-slate-200 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h2 className="font-display font-bold text-xl">
              {editing ? "Modifica subappalto" : "Nuovo subappalto"}
            </h2>
            {editing && (
              <p className="text-xs text-slate-500">L'importo aggiornato ricalcola immediatamente il margine del cantiere.</p>
            )}
          </div>
          <button onClick={onClose} data-testid="btn-close-sub-form"><X className="w-5 h-5 text-slate-500" /></button>
        </div>

        <div className="p-5 space-y-6">
          {/* Anagrafica ditta */}
          <section>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Ditta subappaltatrice</div>
<div className="mb-4 space-y-2">
  <label className="text-xs font-semibold text-slate-700 block">
    Seleziona dall'Agenda Fornitori
  </label>

  <input
    type="text"
    value={ricercaFornitore}
    onChange={(e) => setRicercaFornitore(e.target.value)}
    placeholder="Cerca per ragione sociale o partita IVA..."
    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
    data-testid="sub-cerca-fornitore"
  />

  {ricercaFornitore.trim() && (
    <div className="border border-slate-200 rounded-sm max-h-48 overflow-y-auto bg-white">
      {caricamentoFornitori ? (
        <div className="p-3 text-xs text-slate-500">
          Caricamento fornitori...
        </div>
      ) : fornitoriFiltrati.length === 0 ? (
        <div className="p-3 text-xs text-slate-500">
          Nessun fornitore trovato. Puoi inserire i dati manualmente.
        </div>
      ) : (
        fornitoriFiltrati.map((f, idx) => (
          <button
            key={f.fornitore_id || `${f.ragione_sociale}-${idx}`}
            type="button"
            onClick={() => selezionaFornitore(f)}
            className="w-full text-left px-3 py-2 border-b border-slate-100 hover:bg-orange-50"
          >
            <div className="text-sm font-semibold">
              {f.ragione_sociale}
            </div>
            <div className="text-xs text-slate-500">
              {f.partita_iva
                ? `P.IVA ${f.partita_iva}`
                : "Partita IVA non disponibile"}
            </div>
          </button>
        ))
      )}
    </div>
  )}

  <p className="text-xs text-slate-500">
    Seleziona un fornitore per compilare automaticamente i dati.
    Puoi modificarli prima del salvataggio.
  </p>
</div>
            <div className="grid md:grid-cols-2 gap-4">
              <FormField label="Ragione sociale *" testid="sub-ragione_sociale" value={form.ragione_sociale} onChange={(v) => set("ragione_sociale", v)} />
              <FormField label="Partita IVA" testid="sub-partita_iva" value={form.partita_iva} onChange={(v) => set("partita_iva", v)} />
              <FormField label="Codice fiscale" value={form.codice_fiscale} onChange={(v) => set("codice_fiscale", v)} />
              <FormField label="Referente" value={form.referente} onChange={(v) => set("referente", v)} />
              <FormField label="Telefono" value={form.telefono} onChange={(v) => set("telefono", v)} />
              <FormField label="Email" type="email" value={form.email} onChange={(v) => set("email", v)} />
              <div className="md:col-span-2">
                <FormField label="Indirizzo" value={form.indirizzo} onChange={(v) => set("indirizzo", v)} />
              </div>
            </div>
          </section>

          {/* Contratto */}
          <section>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Contratto</div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-xs font-semibold text-slate-600 block mb-1">Descrizione lavori *</label>
                <textarea
                  rows={2}
                  value={form.descrizione_lavori}
                  onChange={(e) => set("descrizione_lavori", e.target.value)}
                  data-testid="sub-descrizione_lavori"
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  placeholder="Es. Realizzazione impianto elettrico completo…"
                />
              </div>
              <FormField label="Importo imponibile €" type="number" step="0.01" testid="sub-importo" value={form.importo_imponibile} onChange={(v) => set("importo_imponibile", parseFloat(v || 0))} />
              <FormField label="Aliquota IVA %" type="number" step="0.1" value={form.aliquota_iva} onChange={(v) => set("aliquota_iva", parseFloat(v || 0))} />
              <FormField label="Data inizio" type="date" value={form.data_inizio} onChange={(v) => set("data_inizio", v)} />
              <FormField label="Data fine prevista" type="date" value={form.data_fine_prevista} onChange={(v) => set("data_fine_prevista", v)} />
              <FormField label="Data fine effettiva" type="date" value={form.data_fine_effettiva} onChange={(v) => set("data_fine_effettiva", v)} />
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">% avanzamento</label>
                <input
                  type="range" min={0} max={100} step={1}
                  value={form.percentuale_avanzamento || 0}
                  onChange={(e) => set("percentuale_avanzamento", Number(e.target.value))}
                  data-testid="sub-avanzamento"
                  className="w-full"
                />
                <div className="text-xs text-slate-600 mt-1 font-mono">{Math.round(form.percentuale_avanzamento || 0)}%</div>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-3 text-sm bg-slate-50 border border-slate-200 rounded-sm p-3">
              <div><div className="overline">Imponibile</div><div className="font-mono font-bold">{eur(form.importo_imponibile)}</div></div>
              <div><div className="overline">IVA</div><div className="font-mono font-bold">{eur(totIVA)}</div></div>
              <div><div className="overline">Totale IVA incl.</div><div className="font-mono font-bold text-rose-700">{eur(totCon)}</div></div>
            </div>
          </section>

          {editing && <section className="border border-orange-200 bg-orange-50 p-4">
  <label className="flex items-center gap-2 text-sm font-semibold">
    <input type="checkbox" id="invia-contratto" checked={inviaContratto} onChange={(e) => setInviaContratto(e.target.checked)} />
    Invia contratto PDF al subappaltatore via email
  </label>
  <p className="text-xs text-slate-600 mt-2">
    Destinatario: {form.email || "Email non inserita"}
  </p>
</section>}

          {/* Stato & Compliance */}
          <section>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Stato & compliance</div>
            <div className="grid md:grid-cols-3 gap-4 items-end">
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Stato</label>
                <select
                  value={form.stato}
                  onChange={(e) => set("stato", e.target.value)}
                  data-testid="sub-stato"
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white"
                >
                  {Object.entries(STATO_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
                </select>
              </div>
              <FormField label="Scadenza DURC" type="date" value={form.durc_scadenza} onChange={(v) => set("durc_scadenza", v)} />
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer bg-white p-2 border border-slate-200 rounded-sm">
                <input type="checkbox" checked={form.include_in_economia !== false} onChange={(e) => set("include_in_economia", e.target.checked)} data-testid="sub-include-economia" />
                <span className="font-semibold">Includi nell'economia del cantiere</span>
              </label>
            </div>
            <div className="mt-3 grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Contratto firmato (PDF)</label>
                <input
                  type="file"
                  accept="application/pdf,image/*"
                  onChange={(e) => handleFileToBase64(e.target.files?.[0], "contratto_base64")}
                  data-testid="sub-contratto-file"
                  className="w-full text-xs"
                />
                {form.contratto_base64 && (
                  <div className="mt-1 text-[11px] text-emerald-700 inline-flex items-center gap-1">
                    <Paperclip className="w-3 h-3" /> Contratto caricato
                    <button onClick={() => set("contratto_base64", null)} className="ml-2 text-rose-600 hover:underline">rimuovi</button>
                  </div>
                )}
                <AiExtractBox
                  tipo="contratto_subappalto"
                  allegato={form.contratto_base64}
                  onExtracted={(d) => setForm((f) => ({
                    ...f,
                    ragione_sociale: d.ragione_sociale || f.ragione_sociale,
                    partita_iva: d.partita_iva || f.partita_iva,
                    codice_fiscale: d.codice_fiscale || f.codice_fiscale,
                    referente: d.referente || f.referente,
                    telefono: d.telefono || f.telefono,
                    email: d.email || f.email,
                    indirizzo: d.indirizzo || f.indirizzo,
                    descrizione_lavori: d.descrizione_lavori || f.descrizione_lavori,
                    importo_imponibile: d.importo_imponibile > 0 ? d.importo_imponibile : f.importo_imponibile,
                    aliquota_iva: d.aliquota_iva > 0 ? d.aliquota_iva : f.aliquota_iva,
                    data_inizio: /^\d{4}-\d{2}-\d{2}$/.test(d.data_inizio) ? d.data_inizio : f.data_inizio,
                    data_fine_prevista: /^\d{4}-\d{2}-\d{2}$/.test(d.data_fine_prevista) ? d.data_fine_prevista : f.data_fine_prevista,
                    durc_scadenza: /^\d{4}-\d{2}-\d{2}$/.test(d.durc_scadenza) ? d.durc_scadenza : f.durc_scadenza,
                    note: d.note ? (f.note ? `${f.note}\n${d.note}` : d.note) : f.note,
                  }))}
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Note</label>
                <textarea
                  rows={2}
                  value={form.note}
                  onChange={(e) => set("note", e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                />
              </div>
            </div>
          </section>


          {/* Allegati aggiuntivi */}
          <section>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">
              Allegati aggiuntivi
            </div>
            <p className="text-xs text-slate-500 mb-2">
              Puoi caricare più PDF o immagini e aggiungerne altri successivamente.
            </p>
            <input
              type="file"
              multiple
              accept="application/pdf,image/*"
              data-testid="sub-allegati-file"
              className="w-full text-xs"
              onChange={async (e) => {
                const files = Array.from(e.target.files || []);
                e.target.value = "";
                if (!files.length) return;
                if (files.some((file) => file.size > 4 * 1024 * 1024)) {
                  toast.error("Ogni allegato deve essere al massimo 4 MB");
                  return;
                }
                if (files.some((file) => file.type !== "application/pdf" && !file.type.startsWith("image/"))) {
                  toast.error("Sono consentiti soltanto PDF e immagini");
                  return;
                }
                try {
                  const added = await Promise.all(files.map((file) => new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = () => reject(new Error("Lettura file non riuscita"));
                    reader.readAsDataURL(file);
                  })));

                  const total = [...(form.allegati || []), ...added]
                    .reduce((n, a) => n + a.length, 0);
                  if (total > 10 * 1024 * 1024) {
                    toast.error("Troppi allegati: massimo circa 7 MB complessivi");
                    return;
                  }
                  setForm((f) => ({ ...f, allegati: [...(f.allegati || []), ...added] }));
                  toast.success(`${added.length} allegato/i aggiunto/i. Salva il subappalto per confermare.`);
                } catch {
                  toast.error("Errore durante la lettura degli allegati");
                }
              }}
            />
            {(form.allegati || []).length > 0 && (
              <div className="mt-3 space-y-2">
                {(form.allegati || []).map((allegato, index) => (
                  <div key={index} className="flex items-center justify-between gap-3 border border-slate-200 p-2 text-sm">
                    <span className="truncate">Allegato {index + 1}</span>
                    <div className="flex gap-3 shrink-0">
                      <a href={allegato} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">
                        Apri
                      </a>
                      <button
                        type="button"
                        className="text-rose-600 hover:underline"
                        onClick={() => setForm((f) => ({
                          ...f,
                          allegati: (f.allegati || []).filter((_, i) => i !== index),
                        }))}
                      >
                        Rimuovi
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Attività Gantt collegate */}
          {attivitaOptions.length > 0 && (
            <section>
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Attività Gantt collegate</div>
              <div className="border border-slate-200 rounded-sm max-h-48 overflow-y-auto">
                {attivitaOptions.map((a) => (
                  <label key={a.attivita_id} className="flex items-center gap-2 p-2 border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={(form.attivita_collegate || []).includes(a.attivita_id)}
                      onChange={() => toggleAtt(a.attivita_id)}
                      data-testid={`sub-att-${a.attivita_id}`}
                    />
                    <span className="flex-1 truncate">{a.nome}</span>
                    <span className="text-[10px] text-slate-500 font-mono">
                      <CalIcon className="w-3 h-3 inline mr-1" />
                      {a.data_inizio} → {a.data_fine}
                    </span>
                  </label>
                ))}
              </div>
            </section>
          )}
        </div>

        <div className="p-5 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50">Annulla</button>
          <button
            onClick={() => {
              if (!form.ragione_sociale || !form.descrizione_lavori) {
                return toast.error("Compila ragione sociale e descrizione lavori");
              }
              if (inviaContratto) {
                if (!form.email?.trim()) {
                  return toast.error("Inserisci l'email del subappaltatore");
                }
                if (!form.contratto_base64) {
                  return toast.error("Carica il contratto PDF");
                }
                if (!window.confirm(`Inviare il contratto a ${form.email}?`)) {
                  return;
                }
              }
              onSave(inviaContratto);
            }}
            disabled={saving}
            data-testid="btn-save-subappalto"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white font-semibold rounded-sm"
          >
            <Save className="w-4 h-4" />
            {saving ? "Salvataggio…" : editing ? "Salva modifiche" : "Crea subappalto"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FormField({ label, value, onChange, type = "text", step, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600 block mb-1">{label}</label>
      <input
        type={type}
        step={step}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
      />
    </div>
  );
}

// ------------------- DETAIL (pagamenti) -------------------

function SubappaltoDetail({ subappalto, onClose, onChanged }) {
  const qc = useQueryClient();
  const [pForm, setPForm] = useState({ data: isoDate(), importo: 0, tipo: "sal", descrizione: "", quietanzato: false });
  const totale = Number(subappalto.importo_totale || 0);
  const pagato = (subappalto.pagamenti || []).filter((p) => p.quietanzato).reduce((s, p) => s + Number(p.importo || 0), 0);
  const residuo = Math.max(totale - pagato, 0);

  const addPag = useMutation({
    mutationFn: () => api.post(`/subappalti/${subappalto.subappalto_id}/pagamenti`, pForm).then((r) => r.data),
    onSuccess: () => {
      toast.success("Pagamento aggiunto");
      setPForm({ data: isoDate(), importo: 0, tipo: "sal", descrizione: "", quietanzato: false });
      qc.invalidateQueries({ queryKey: ["subappalti"] });
      onChanged();
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const delPag = useMutation({
    mutationFn: (pid) => api.delete(`/subappalti/${subappalto.subappalto_id}/pagamenti/${pid}`).then((r) => r.data),
    onSuccess: () => {
      toast.success("Pagamento rimosso");
      qc.invalidateQueries({ queryKey: ["subappalti"] });
      onChanged();
    },
  });

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl border border-slate-200 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="font-display font-bold text-lg">{subappalto.ragione_sociale}</h2>
            <p className="text-xs text-slate-500">Pagamenti / SAL</p>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-2 text-sm">
            <div className="p-2 bg-slate-50 rounded-sm border border-slate-200">
              <div className="overline">Totale contratto</div>
              <div className="font-mono font-bold">{eur(totale)}</div>
            </div>
            <div className="p-2 bg-emerald-50 rounded-sm border border-emerald-200">
              <div className="overline">Pagato</div>
              <div className="font-mono font-bold text-emerald-800">{eur(pagato)}</div>
            </div>
            <div className={`p-2 rounded-sm border ${residuo > 0 ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200"}`}>
              <div className="overline">Residuo</div>
              <div className={`font-mono font-bold ${residuo > 0 ? "text-amber-800" : "text-slate-800"}`}>{eur(residuo)}</div>
            </div>
          </div>

          {(subappalto.pagamenti || []).length > 0 ? (
            <table className="w-full text-xs border border-slate-200">
              <thead className="bg-slate-50 text-slate-600 uppercase tracking-widest">
                <tr>
                  <th className="text-left p-2">Data</th>
                  <th className="text-left p-2">Tipo</th>
                  <th className="text-right p-2">Importo</th>
                  <th className="text-left p-2">Descrizione</th>
                  <th className="text-center p-2">Pag.</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {subappalto.pagamenti.map((p) => (
                  <tr key={p.pagamento_id} className="border-t border-slate-100">
                    <td className="p-2">{p.data}</td>
                    <td className="p-2 uppercase font-bold">{p.tipo}</td>
                    <td className="p-2 text-right font-mono">{eur(p.importo)}</td>
                    <td className="p-2 text-slate-600">{p.descrizione}</td>
                    <td className="p-2 text-center">{p.quietanzato ? <CheckCircle2 className="w-4 h-4 text-emerald-600 inline" /> : <Ban className="w-4 h-4 text-slate-400 inline" />}</td>
                    <td className="p-2 text-right">
                      <button onClick={() => window.confirm("Rimuovere questo pagamento?") && delPag.mutate(p.pagamento_id)} className="text-rose-600 hover:text-rose-800">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-xs text-slate-400 italic">Nessun pagamento registrato.</div>
          )}

          <div className="border-t border-slate-200 pt-4">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Nuovo pagamento / SAL</div>
            <div className="grid grid-cols-2 gap-2">
              <input type="date" value={pForm.data} onChange={(e) => setPForm({ ...pForm, data: e.target.value })} className="px-2 py-1.5 border border-slate-300 rounded-sm text-sm" data-testid="pag-data" />
              <select value={pForm.tipo} onChange={(e) => setPForm({ ...pForm, tipo: e.target.value })} className="px-2 py-1.5 border border-slate-300 rounded-sm text-sm bg-white" data-testid="pag-tipo">
                {TIPI_PAGAMENTO.map((t) => <option key={t.k} value={t.k}>{t.l}</option>)}
              </select>
              <input type="number" step="0.01" placeholder="Importo €" value={pForm.importo || ""} onChange={(e) => setPForm({ ...pForm, importo: parseFloat(e.target.value || 0) })} className="px-2 py-1.5 border border-slate-300 rounded-sm text-sm font-mono" data-testid="pag-importo" />
              <input type="text" placeholder="Descrizione (opz.)" value={pForm.descrizione} onChange={(e) => setPForm({ ...pForm, descrizione: e.target.value })} className="px-2 py-1.5 border border-slate-300 rounded-sm text-sm" />
              <label className="inline-flex items-center gap-2 text-xs col-span-2">
                <input type="checkbox" checked={pForm.quietanzato} onChange={(e) => setPForm({ ...pForm, quietanzato: e.target.checked })} data-testid="pag-quietanzato" />
                Quietanzato (importo effettivamente pagato)
              </label>
            </div>
            <button
              onClick={() => addPag.mutate()}
              disabled={!pForm.importo || addPag.isPending}
              data-testid="btn-add-pagamento"
              className="mt-2 px-3 py-2 text-sm bg-slate-800 hover:bg-slate-900 disabled:opacity-60 text-white font-semibold rounded-sm"
            >
              Aggiungi pagamento
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
